/**
 * PopupErrorBoundary Component
 *
 * Lightweight error boundary for modal popups and dialogs.
 * Shows inline error without crashing the parent component.
 *
 * @example
 * ```typescript
 * <PopupErrorBoundary onClose={() => setIsOpen(false)}>
 *   <TaskEditPopup ... />
 * </PopupErrorBoundary>
 * ```
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/utils/logger';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Icon, IconName } from '@/icons';
import { useThemeColors } from '@/styles/colors/useThemeColors';

// eslint-disable-next-line react-refresh/only-export-components -- class component file cannot fast-refresh
const ErrorIcon = () => {
  const colors = useThemeColors();
  return (
    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
      <Icon name={IconName.ALERT_CIRCLE} size="xl" color={colors.error} />
    </div>
  );
};

interface PopupErrorBoundaryProps {
  /** Child components to protect */
  children: ReactNode;
  /** Callback to close the popup */
  onClose?: () => void;
  /** Whether the popup is open */
  isOpen?: boolean;
}

interface PopupErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary for popup components.
 * Prevents popup errors from crashing the entire app.
 */
export class PopupErrorBoundary extends Component<
  PopupErrorBoundaryProps,
  PopupErrorBoundaryState
> {
  constructor(props: PopupErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<PopupErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('Popup Error Boundary caught error', error, {
      component_stack: errorInfo.componentStack,
      error_boundary: 'PopupErrorBoundary',
    });
  }

  // Reset error state when popup closes
  componentDidUpdate(prevProps: PopupErrorBoundaryProps): void {
    if (prevProps.isOpen && !this.props.isOpen && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleClose = (): void => {
    this.setState({ hasError: false, error: null });
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Dialog open={true} onOpenChange={this.handleClose}>
          <DialogContent className="sm:max-w-md">
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              {/* Error Icon */}
              <ErrorIcon />

              {/* Error Message */}
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Something Went Wrong
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                We encountered an unexpected error. This has been logged and
                we'll look into it.
              </p>

              {/* Development Error Details */}
              {import.meta.env.DEV && this.state.error && (
                <div className="w-full bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                  <p className="text-xs font-semibold text-red-800 mb-1">
                    Error Details (Development Only):
                  </p>
                  <p className="text-xs text-red-700 font-mono break-all text-left">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              {/* Close Button */}
              <div className="flex gap-3">
                <Button onClick={this.handleClose} className="px-6">
                  Close
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="px-6"
                >
                  Reload Page
                </Button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                If this problem persists, please contact support.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    return this.props.children;
  }
}
